import { Fragment, useEffect, useState } from 'react';

import { InvoiceService } from '../../hoa-don/InvoiceService';
import { RegisterInvoiceService } from '../../../service/invoice/RegisterInvoiceService';
import {
  AdjustmentReasonOption,
  OneBtsCatalogService,
} from '../../../service/invoice/OneBtsCatalogService';
import { CurrencyService } from '../../danh-muc/tien-te/CurrencyService';

import { useToolbar } from '../../../shared/hooks/Toolbar';
import { useDataTable } from '../../../shared/hooks/DataTable';

import { DialogCrup } from '../../hoa-don/component/DialogCrup';
import { DialogSelectInvoice } from '../../hoa-don/component/DialogSelectInvoice';
import { DialogView } from '../../hoa-don/component/DialogView';

import { DialogStatement } from '../components/DialogStatement';
import { DialogAttachment } from '../components/DialogAttachment';
import { useSign } from '../../../shared/component/Sign';
import { StatementInvoiceService } from '../StatementInvoiceService';
import { DialogSendEmail } from '../../hoa-don/component/DialogSendEmail';
import { DialogRegisteredAssetInvoiceWarning } from '../../hoa-don/component/DialogRegisteredAssetInvoiceWarning';
import { ConfirmDialogService } from '../../../AppConfirmDialog';
import {
  getRegisteredAssetTypeCompany,
  isMachineInvoice,
  isRegisteredAssetInvoice,
} from '../../../utils/invoiceHelper';
import { useTranslation } from '../../../shared/language';

export const List = () => {

  const { t } = useTranslation();
  const header = t('menu.invoiceAdjustment');
  const dataKey = 'invoiceId';

  const [displaySelectInvoice, setDisplaySelectInvoice] = useState(null);

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [displayCrup, setDisplayCrup] = useState(false);
  const [registerInvoice, setRegisterInvoice] = useState(null);
  const [registerInvoices, setRegisterInvoices] = useState([]);
  const [currencies, setCurrencies] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState(null);

  const [displayStatement, setDisplayStatement] = useState(false);
  const [displayUpload, setDisplayUpload] = useState(false);
  const [displayView, setDisplayView] = useState(false);
  const [documentType, setDocumentType] = useState(InvoiceService.documentType_Adjust);
  const [displayRegisteredAssetWarning, setDisplayRegisteredAssetWarning] = useState(false);
  const [displaySendEmail, setDisplaySendEmail] = useState(false);
  const [data, setData] = useState(null);
  const [reasonOptions, setReasonOptions] = useState<AdjustmentReasonOption[]>([]);
  const print = (invoice) => {
    StatementInvoiceService.getByInvoice(invoice.invoiceId).then((res) => {
      window.open(StatementInvoiceService.getPdfUrl(res.id), '_blank');
    })
  }

  useEffect(() => {
    RegisterInvoiceService.getUsingList({ maxResultCount: 1000 }).then(({ items }) => {
      const _registerInvoices = [];
      items.forEach(item => {
        item.label = `${item.symbolCode} - ${item.invoiceTypeName}`;
        _registerInvoices.push(item);
      });
      setRegisterInvoices(_registerInvoices);
      setRegisterInvoice(_registerInvoices[0]);
      if (_registerInvoices.some((item) => Number(item.typeCompany) === 9)) {
        OneBtsCatalogService.getAdjustmentReasonCatalog(
          InvoiceService.documentType_Adjust,
        )
          .then((response) =>
            setReasonOptions(
              Array.isArray(response)
                ? response
                : response?.options || response?.items || [],
            ),
          )
          .catch(() => setReasonOptions([]));
      }
    });
    CurrencyService.getList({}).then(({ items }) => {
      setCurrencies(items);
    });
    InvoiceService.getPaymentTypes().then(({ items }) => {
      const _paymentTypes = [];
      items.forEach(item => {
        _paymentTypes.push({ value: item.name, label: item.name });
      });
      setPaymentTypes(_paymentTypes);
    });
  }, []);

  const { render: renderDataTable, reloadLazyData, selectedItems } = useDataTable({
    header,
    dataKey,
    headerColumnGroups: [
      { header: t('menu.invoiceAdjustment'), colSpan: 8, },
      { header: t('common.beingAdjusted'), colSpan: 4 }
    ],
    columns: [
      { field: 'invoiceSerial', header: t('common.symbol'), width: 100, matchMode: 'contains' },
      {
        field: 'invoiceNumber', header: t('common.invoiceNo'), width: 110, matchMode: 'contains', dataType: 'custom', customCell(rowData): any {
          const text = rowData.invoiceNumber;
          return (
            <span className="underline text-primary" onClick={() => viewInvoice(rowData.invoiceId)}>{text}</span >
          );
        }, style: { justifyContent: 'center' }
      },
      { field: 'invoiceDate', header: t('common.invoiceDate'), width: 120, dataType: 'date' },
      {
        field: 'sendTaxStatus', header: t('common.submissionStatus'), width: 180, matchMode: 'contains', filterType: 'dropdown', filterOptions: InvoiceService.sendTaxStatuses, dataType: 'custom', customCell(rowData: any): any {
          if ([3, 4, 5].includes(rowData.sendTaxStatus)) {
            return <span className={`underline send-tax-status send-tax-status-${rowData.sendTaxStatus}`}>{InvoiceService.getSendTaxStatusLabel(rowData.sendTaxStatus)}</span>;
          }
          return <span className={`send-tax-status send-tax-status-${rowData.sendTaxStatus}`}>{InvoiceService.getSendTaxStatusLabel(rowData.sendTaxStatus)}</span>;
        }
      },
      {
        field: 'buyerLegalName', header: t('catalog.customer'), minWidth: 200, matchMode: 'contains', dataType: 'custom', customCell(rowData: any): any {
          return rowData.buyerLegalName || rowData.buyerDisplayName;
        }
      },
      {
        field: '', header: t('common.statement'), width: 110, sortable: false, dataType: 'custom', customCell(rowData): any {
          const text = rowData.hasDocument ? rowData.documentNo : t('common.statementCreate');
          const className = rowData.hasDocument ? "underline send-tax-status send-tax-status-4" : "underline text-primary";
          return (
            <span className={className} onClick={() => setDisplayStatement(true)}>{text}</span>
          );
        }, style: { justifyContent: 'center' }
      },
      {
        field: '', header: t('common.actions'), width: 100, sortable: false, dataType: 'custom', customCell(rowData: any): any {
          return (
            <div className='flex gap-3'>
              <i className="pi pi-link" title={t('common.attachment')} onClick={() => setDisplayUpload(true)}></i>
              {rowData.hasDocument && <i className="pi pi-print" title={t('common.printInvoice')} onClick={() => print(rowData)}></i>}
              <i className="pi pi-envelope" title={t('common.emailStatementForSignature')} onClick={() => {

                setDisplaySendEmail(true)
                StatementInvoiceService.getByInvoice(rowData.invoiceId).then((res) => {
                  setData(res)
                })
              }
              }></i>
            </div>
          );
        }, style: { justifyContent: 'center' }
      },
      { field: 'relatedInvoiceSerial', header: t('common.symbol'), width: 100, matchMode: 'contains' },
      {
        field: 'relatedInvoiceNumber', header: t('common.invoiceNo'), width: 110, matchMode: 'contains', dataType: 'custom', customCell(rowData): any {
          const text = rowData.relatedInvoiceNumber;
          return (
            <span className="underline text-primary" onClick={() => viewInvoice(rowData.relatedInvoiceId)}>{text}</span >
          );
        }, style: { justifyContent: 'center' }
      },
      { field: 'relatedInvoiceDate', header: t('common.invoiceDate'), width: 120, dataType: 'date' },
      {
        field: 'invoiceNote',
        header: t('common.invoiceReason'),
        width: 110,
        matchMode: 'contains',
        dataType: 'custom',
        customCell(rowData): any {
          if (!isRegisteredAssetInvoice(rowData, registerInvoices)) {
            return rowData.invoiceNote || '';
          }
          const reasonValue = rowData.reasons ?? rowData.invoiceNote ?? '';
          return reasonOptions.find(
            (option) => String(option.value) === String(reasonValue),
          )?.label || reasonValue;
        },
      }
    ],
    indexColumnWidth: 40,
    getList: (params) => {
      if (!params.sortType) {
        params.sortType = 'DESCEND';
      }
      if (!params.sorting) {
        params.sorting = 'invoiceDate';
      }
      params.IsOtherInvoice = false;
      return InvoiceService.getListAdjustments(params);
    },
    selectionMode: 'single'
  });

  // API danh sách không trả typeCompany nên bổ sung từ mẫu đăng ký trước khi mở biên bản.
  const selectedItemsForStatement = Array.isArray(selectedItems)
    ? selectedItems.map((item) => ({
      ...item,
      typeCompany: getRegisteredAssetTypeCompany(item, registerInvoices),
    }))
    : selectedItems && {
      ...(selectedItems as Record<string, any>),
      typeCompany: getRegisteredAssetTypeCompany(selectedItems as any, registerInvoices),
    };

  const viewInvoice = (id) => {
    setSelectedInvoice({
      id
    });
    setDisplayView(true);
  }

  const { renderToolbar } = useToolbar({
    dataKey,
    reloadLazyData,
    otherActions: [
      {
        label: t('common.registerAdjustmentInvoice'), icon: 'pi pi-fw pi-user-edit', className: 'iam-toolbar', command: () => {
          setDisplaySelectInvoice(true);
        }
      }
    ]
  });

  const handleAdjustmentFormOpen = (item) => {
    setDocumentType(InvoiceService.documentType_Adjust);
    setSelectedInvoice(item);
    setDisplayCrup(true);
    setDisplaySelectInvoice(false);
  };

  const onAddSelectedItems = (items) => {
    if (!items) {
      return;
    }

    const item = Array.isArray(items) ? items[0] : items;
    setSelectedInvoice(item);
    const selectedRegisterInvoice = registerInvoices.find(
      (registerItem) =>
        (item.registerInvoiceId &&
          String(registerItem.id) === String(item.registerInvoiceId)) ||
        (item.invoiceSerial && registerItem.symbolCode === item.invoiceSerial),
    );
    if (selectedRegisterInvoice) {
      // Form động phải lấy đúng cấu hình của mẫu hóa đơn gốc để gắn danh mục lý do vào field legacy.
      setRegisterInvoice(selectedRegisterInvoice);
    }

    // Cảnh báo được đặt trước kiểm tra máy tính tiền để người dùng thấy đúng quy định của typeCompany=9.
    if (isRegisteredAssetInvoice(item, registerInvoices)) {
      setDocumentType(InvoiceService.documentType_Adjust);
      setDisplaySelectInvoice(false);
      setDisplayRegisteredAssetWarning(true);
      return true;
    }

    if (isMachineInvoice(item?.invoiceSerial)) {
      ConfirmDialogService.confirmAdjustMachineInvoice(
        () => handleAdjustmentFormOpen(item),
        () => {
          // Giữ popup chọn để người dùng có thể đổi hóa đơn khi từ chối cảnh báo.
        },
      );
      return true;
    }

    handleAdjustmentFormOpen(item);
  };

  const handleAdjustmentWarningClose = () => {
    setDisplayRegisteredAssetWarning(false);
    setSelectedInvoice(null);
  };

  const handleAdjustmentWarningReplace = () => {
    setDisplayRegisteredAssetWarning(false);
    setDocumentType(InvoiceService.documentType_Substitute);
    setDisplayCrup(true);
  };

  const handleAdjustmentWarningContinue = () => {
    setDisplayRegisteredAssetWarning(false);
    if (isMachineInvoice(selectedInvoice?.invoiceSerial)) {
      ConfirmDialogService.confirmAdjustMachineInvoice(
        () => handleAdjustmentFormOpen(selectedInvoice),
        () => {
          // Mở lại popup chọn để người dùng có thể đổi hóa đơn sau khi từ chối cảnh báo máy tính tiền.
          setDisplaySelectInvoice(true);
        },
      );
      return;
    }

    handleAdjustmentFormOpen(selectedInvoice);
  };

  const { renderSign, sign } = useSign({ onComplete: reloadLazyData });

  return (
    <Fragment>
      {renderToolbar()}
      {renderDataTable()}

      {renderSign()}

      <DialogRegisteredAssetInvoiceWarning
        visible={displayRegisteredAssetWarning}
        onHide={handleAdjustmentWarningClose}
        onReplace={handleAdjustmentWarningReplace}
        onContinueAdjustment={handleAdjustmentWarningContinue}
      />

      <DialogSelectInvoice display={displaySelectInvoice} setDisplay={setDisplaySelectInvoice} addSelectedItems={onAddSelectedItems} selectionMode="radiobutton"
        invoiceStatus="0,5" sendTaxStatus="2,4" />

      <DialogCrup display={displayCrup} setDisplay={setDisplayCrup} reloadLazyData={reloadLazyData}
        registerInvoices={registerInvoices} currencies={currencies} paymentTypes={paymentTypes}
        registerInvoice={registerInvoice} selectedItem={selectedInvoice} invoiceStatus={documentType}
        setInvoiceStatus={setDocumentType} sign={sign} />

      <DialogAttachment display={displayUpload} setDisplay={setDisplayUpload} selectedItem={selectedItems} />

      <DialogStatement display={displayStatement} setDisplay={setDisplayStatement} selectedItem={selectedItemsForStatement} registerInvoices={registerInvoices} enableBts01ReasonCatalog documentType={documentType} reloadLazyData={reloadLazyData} title={documentType === InvoiceService.documentType_Substitute ? t('common.replacement') : t('common.adjustment')} />

      <DialogView display={displayView} setDisplay={setDisplayView} selectedItem={selectedInvoice} />
      <DialogSendEmail display={displaySendEmail} setDisplay={setDisplaySendEmail} selectedItems={data} sendType={8} />
    </Fragment>
  )
}
